'use client';

import { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQS = [
  {
    cat: 'Commandes & Livraison',
    items: [
      {
        q: 'Quels sont les délais de livraison ?',
        a: 'Belgique : 24-48h ouvrées. Europe : 3-5 jours. Monde : 5-10 jours. Retrait boutique gratuit à Anvers sous 24h. Chaque commande est confirmée personnellement par le maître artisan.',
      },
      {
        q: 'La livraison est-elle offerte ?',
        a: 'Oui, dès 500 € d\'achat en Belgique. En Europe et Monde, des frais fixes de 35 € et 65 € s\'appliquent, indépendamment du montant. Le retrait boutique est toujours gratuit.',
      },
      {
        q: 'Comment suivre ma commande ?',
        a: 'Vous recevez un email de confirmation avec numéro de commande MAT-XXXXXXXX. Un suivi SMS vous est envoyé à chaque étape : façonnage (si sur mesure), expédition, livraison. QR blockchain accessible à tout moment.',
      },
      {
        q: 'Puis-je modifier ou annuler ma commande ?',
        a: 'Oui, sous 24h après la commande pour les pièces en stock. Pour les commandes sur mesure, modification possible uniquement avant le stade "Coupe & Couture" (Jour 4 du processus).',
      },
    ],
  },
  {
    cat: 'Paiement & Sécurité',
    items: [
      {
        q: 'Quels modes de paiement acceptez-vous ?',
        a: 'Carte bancaire (Visa, Mastercard, Amex), virement bancaire SEPA, paiement via WhatsApp pour les commandes informelles. Cryptomonnaies acceptées sur demande (Bitcoin, Ethereum) via la blockchain Smartest Capital.',
      },
      {
        q: 'Le paiement est-il sécurisé ?',
        a: 'Oui, transaction chiffrée SSL 256-bit. Vos données bancaires ne sont jamais stockées. Paiement traité par prestataire PCI-DSS Level 1. Aucune information sensible transmise à la maison.',
      },
      {
        q: 'Proposez-vous le paiement en plusieurs fois ?',
        a: 'Oui, en 3 ou 4 fois sans frais pour les commandes supérieures à 500 €, via notre partenaire bancaire. Disponible au checkout. Une pièce d\'exception peut s\'étaler sur 3 mois.',
      },
    ],
  },
  {
    cat: 'Sur Mesure & Façonnage',
    items: [
      {
        q: 'Comment commander une pièce sur mesure ?',
        a: 'Rendez-vous à l\'atelier d\'Anvers (sur rendez-vous) ou contactez-nous via le formulaire. Le maître artisan vous reçoit, écoute votre souhait, croquine à la main. Délai : 12 jours, 5 étapes, 1 pièce unique au monde.',
      },
      {
        q: 'Puis-je faire graver mes initiales ?',
        a: 'Oui, monogramme gravé au fer chaud ou cousu main selon la pièce. Service inclus pour les commandes sur mesure. Pour les pièces en stock, supplément de 25 € à 60 € selon la complexité.',
      },
      {
        q: 'Quelle est la différence entre cuir pleine fleur et nappa ?',
        a: 'Le cuir pleine fleur est le cuir le plus noble : non corrigé, il conserve la grain naturel de la peau. Le nappa est une finition plus souple, obtenue par tannage prolongé. Les deux sont d\'exception, le choix dépend de l\'usage.',
      },
    ],
  },
  {
    cat: 'Garantie & Entretien',
    items: [
      {
        q: 'Que couvre la garantie à vie ?',
        a: 'Tous les défauts de fabrication : coutures, hardware, doublure. Réparation atelier gratuite à perpétuité. La garantie ne couvre pas l\'usure normale, les dommages dus à une négligence ou un mauvais entretien.',
      },
      {
        q: 'Comment entretenir mon cuir ?',
        a: 'Évitez l\'eau prolongée, séchez à l\'air libre si pluie. Tous les 6 mois, nourrissez avec une cire d\'abeille spéciale cuir (disponible à la boutique). Évitez le soleil direct prolongé. Stockage dans son écrin à l\'abri de l\'humidité.',
      },
      {
        q: 'Proposez-vous un service de réparation ?',
        a: 'Oui, à vie. Pour les pièces MATAR : gratuit (garantie). Pour les cuirs d\'autres maisons : devis sur place. Délai moyen 2-3 semaines. Une pièce réparée conserve sa traçabilité blockchain et son numéro.',
      },
      {
        q: 'Mon cuir peut-il être reconditionné ?',
        a: 'Oui, service de reconditionnement complet : nettoyage, re-patine, polissage, remplacement hardware si besoin. 180 € à 380 € selon la pièce. Votre cuir renaît comme au premier jour.',
      },
    ],
  },
  {
    cat: 'Authenticité & Traçabilité',
    items: [
      {
        q: 'Comment vérifier l\'authenticité de ma pièce ?',
        a: 'Chaque pièce porte un QR code gravé au dos. Scannez-le avec votre téléphone pour accéder au dossier complet : origine du cuir, artisan, date de façonnage, numéro de série, certificat d\'authenticité. Données inscrites sur blockchain Smartest Capital.',
      },
      {
        q: 'Ma pièce est-elle vraiment unique ?',
        a: 'Chaque pièce MATAR est numérotée. Les éditions limitées sont strictement limitées (50 à 500 exemplaires selon les collections). Les pièces sur mesure sont, elles, véritablement uniques au monde.',
      },
      {
        q: 'D\'où viennent vos cuirs ?',
        a: 'Tanneries françaises et italiennes pour le cuir pleine fleur européen. Ranch DALLU NGAAY au Sénégal pour les cuirs panafricains. Tannage végétal 42 jours, sans produits chimiques agressifs. Certificat d\'origine fourni avec chaque pièce.',
      },
    ],
  },
];

export default function FAQ() {
  const [open, setOpen] = useState<string | null>('0-0');

  return (
    <section
      id="faq"
      className="relative py-24 sm:py-32 bg-[#0a0a0f] border-t border-[#D4AF37]/10 overflow-hidden"
    >
      <div className="relative max-w-4xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block h-px w-10 bg-[#D4AF37]" />
            <span className="eyebrow">Questions Fréquentes</span>
            <span className="block h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
            <span className="text-gold-gradient">Tout savoir,</span>{' '}
            <span className="text-[#F4E8C1]">avant d'investir.</span>
          </h2>
          <p className="max-w-2xl mx-auto text-[#F4E8C1]/65 font-light leading-relaxed">
            Les réponses aux questions les plus posées par nos clients. Pour
            toute autre interrogation, le maître artisan vous répond personnellement.
          </p>
        </div>

        {/* FAQ accordion by category */}
        <div className="space-y-8">
          {FAQS.map((cat, catIdx) => (
            <div key={cat.cat}>
              {/* Category header */}
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-[#D4AF37]/20">
                <HelpCircle size={18} className="text-[#D4AF37]" />
                <h3 className="font-display text-lg font-semibold text-gold-gradient uppercase tracking-[0.15em]">
                  {cat.cat}
                </h3>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/45 ml-auto">
                  {cat.items.length} questions
                </span>
              </div>

              {/* Items */}
              <div className="space-y-2">
                {cat.items.map((item, itemIdx) => {
                  const key = `${catIdx}-${itemIdx}`;
                  const isOpen = open === key;
                  return (
                    <div
                      key={key}
                      className={`border transition-all duration-300 ${
                        isOpen
                          ? 'border-[#D4AF37]/45 bg-[#12121a]/70'
                          : 'border-[#D4AF37]/15 hover:border-[#D4AF37]/30'
                      }`}
                    >
                      <button
                        onClick={() => setOpen(isOpen ? null : key)}
                        className="w-full flex items-center justify-between gap-4 p-4 sm:p-5 text-left"
                        aria-expanded={isOpen}
                      >
                        <span className="text-sm font-medium text-[#F4E8C1] pr-4">
                          {item.q}
                        </span>
                        <ChevronDown
                          size={16}
                          className={`text-[#D4AF37] flex-shrink-0 transition-transform duration-300 ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <div className="px-4 sm:px-5 pb-5 pt-0">
                          <div className="pt-2 border-t border-[#D4AF37]/15">
                            <p className="text-sm text-[#F4E8C1]/70 leading-relaxed font-light pt-3">
                              {item.a}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-8">
          <h3 className="font-display text-xl font-semibold text-[#F4E8C1] mb-3 tracking-wide">
            Vous ne trouvez pas votre réponse ?
          </h3>
          <p className="text-sm text-[#F4E8C1]/65 mb-6 max-w-md mx-auto font-light leading-relaxed">
            Le maître artisan vous répond personnellement sous 24 heures.
            Aucune question n'est trop petite, aucune exigence trop grande.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center justify-center gap-3 px-8 py-3.5 bg-gold-gradient text-[#0a0a0f] font-semibold uppercase tracking-[0.18em] text-xs hover:glow-gold transition-all duration-400"
          >
            Poser une question
          </a>
        </div>
      </div>
    </section>
  );
}
